/**
 * P21 B4a – tính công một ngày theo lịch nhóm (docs/phases/P21.md §4.3).
 * Giờ Việt Nam UTC+7 cố định (không có giờ mùa hè). Hàm thuần, dùng chung API (bảng công) và web (xem trước).
 */

export const WORK_GROUP_LABELS = {
  office: 'Văn phòng',
  warehouse: 'Kho',
  driver: 'Tài xế',
  construction: 'Thi công',
  seasonal: 'Thời vụ',
} as const;
export type WorkGroup = keyof typeof WORK_GROUP_LABELS;

/**
 * Khối bộ phận (anh Tiến 15/10/2026, DEC-354): lọc, xem theo khối thay cho "nhóm lao động".
 * Mỗi phòng ban thuộc 1 khối; nhóm lao động (cách chấm công) tự suy ra từ khối + hồ sơ tài xế ở Đội xe, không nhập tay.
 */
export const DIVISION_LABELS = {
  office: 'Văn Phòng',
  logistics: 'Kho - Vận',
  workshop: 'Xưởng',
  construction: 'Công Trình',
} as const;
export type Division = keyof typeof DIVISION_LABELS;
export const DIVISIONS = Object.keys(DIVISION_LABELS) as Division[];

export const ATTENDANCE_SOURCE_LABELS = {
  self: 'Tự chấm',
  crew: 'Tổ trưởng chấm',
  manual: 'Văn phòng nhập',
  vehicle: 'Nhận / trả xe',
  crm: 'Từ CRM',
} as const;
export type AttendanceSource = keyof typeof ATTENDANCE_SOURCE_LABELS;

export const ATTENDANCE_FLAG_LABELS = {
  no_gps: 'Không có vị trí GPS',
  offline: 'Gửi trễ (mất mạng)',
  missing_in: 'Thiếu giờ vào',
  missing_out: 'Thiếu giờ ra',
  off_day: 'Đi làm ngày nghỉ',
  adjusted: 'Đã sửa tay',
  auto_closed: 'Tự chốt ra ca (quên chấm ra)',
  new_device: 'Chấm bằng điện thoại chưa đăng ký',
  shared_device: 'Điện thoại đã chấm cho người khác trong ngày',
} as const;
export type AttendanceFlag = keyof typeof ATTENDANCE_FLAG_LABELS;

export const WEEKDAY_SHORT = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'] as const;

export interface ScheduleRule {
  calcMode: 'hourly' | 'daily';
  /** 'HH:MM' hoặc 'HH:MM:SS' */
  startTime: string | null;
  endTime: string | null;
  breakStart: string | null;
  breakMinutes: number;
  /** 0 = Chủ nhật … 6 = Thứ bảy */
  weeklyOff: number[];
  lateGraceMinutes: number;
  /** P21 C1: 1 = vào sáng, ra chiều; 2 = vào/ra mỗi buổi (4 lần); 3 = vào sáng, vào chiều (không chấm ra, DEC-248). Thiếu = 1 (lịch cũ). */
  punchMode?: PunchMode;
  /** P21 C2: ra trong X phút cuối ca (buổi) vẫn tính đủ giờ. Thiếu = 0 (lịch cũ). */
  earlyGraceMinutes?: number;
  /** Quên chấm ra: sau giờ hết ca (buổi) + X phút tính như ra đúng giờ hết ca, gắn cờ auto_closed. null/thiếu = không tự chốt. */
  autoCloseMinutes?: number | null;
  /**
   * Giờ hết công chuẩn (DEC-532). null/thiếu = giờ ra ca. Lịch Kho 07:00–19:00 nhưng chuẩn công 07:00–17:00:
   * công, về sớm tính tới giờ này; ở lại tới giờ ra ca hay sau đó đều không tính ngoài giờ (tăng ca chỉ theo lệnh).
   */
  creditEndTime?: string | null;
  /**
   * DEC-820: giờ được về của NV thuộc "Nhóm được về sớm" (thứ đó). Chấm ra từ giờ này tới giờ hết công = đủ công, không tính về sớm.
   * Chỉ nới mốc cuối ngày (không nới giờ ra trưa của lịch chấm 4 lần). null/thiếu = như lịch.
   */
  earlyLeaveTime?: string | null;
}

/** DEC-820: số phút được về trước giờ hết công (dung sai cuối ngày) = max(dung sai về sớm của lịch, giờ hết công − giờ được về). */
export function endOfDayGrace(rule: ScheduleRule): number {
  const g = rule.earlyGraceMinutes ?? 0;
  const end = creditEnd(rule);
  if (!rule.earlyLeaveTime || !end || !rule.startTime) return g;
  const allowed = Math.max(timeToMin(rule.startTime), timeToMin(rule.earlyLeaveTime));
  return Math.max(g, timeToMin(end) - allowed);
}

export const PUNCH_MODES = [1, 3, 2] as const;
export type PunchMode = (typeof PUNCH_MODES)[number];
export const PUNCH_MODE_LABELS: Record<PunchMode, string> = {
  1: 'Vào sáng – ra chiều',
  3: 'Vào sáng – vào chiều',
  2: 'Vào, ra sáng – vào, ra chiều',
};
/** Lịch tách 2 buổi (cần ca sáng, ca chiều): kiểu 2 và 3 */
export const isSplitPunch = (m: PunchMode | undefined | null) => m === 2 || m === 3;

export interface DayRecord {
  /** Chấm 2 lần/ngày: checkIn/checkOut = buổi sáng */
  checkIn: string | null;
  checkOut: string | null;
  pmCheckIn?: string | null;
  pmCheckOut?: string | null;
  dayFraction: number | null;
  otHours: number;
  flags?: string[];
  /** Nguồn dòng chấm công; 'crm' = chuyển từ Minhtien-CRM (DEC-347) */
  source?: string | null;
}

export interface DayResult {
  /** Số công của ngày, 2 chữ số lẻ */
  work: number;
  workedMinutes: number | null;
  lateMinutes: number;
  earlyMinutes: number;
  /** Phút ở lại sau giờ ra ca (chỉ để tham khảo; OT tính khi có duyệt, B3) */
  afterMinutes: number;
  otHours: number;
  offDay: boolean;
  flags: AttendanceFlag[];
  /** Công theo buổi (mỗi buổi tối đa phần của buổi đó trong ca) – cho kiểu xem Sáng | Chiều. null = lịch không có nghỉ trưa / theo ngày */
  sessions: { am: number; pm: number } | null;
}

const VN_OFFSET_MIN = 7 * 60;

/** Phút kể từ 00:00 giờ VN của thời điểm ISO. */
export function vnMinuteOfDay(iso: string): number {
  const t = new Date(iso).getTime();
  const m = Math.floor((t / 60_000 + VN_OFFSET_MIN) % 1440);
  return m < 0 ? m + 1440 : m;
}

/** Ngày giờ VN (YYYY-MM-DD) của thời điểm ISO. */
export function vnDateOf(iso: string | Date): string {
  const t = typeof iso === 'string' ? new Date(iso).getTime() : iso.getTime();
  return new Date(t + VN_OFFSET_MIN * 60_000).toISOString().slice(0, 10);
}

/** Thời điểm UTC (ISO) của ngày VN + 'HH:MM'. */
export function vnDateTimeToIso(date: string, hhmm: string): string {
  const h = Number(hhmm.slice(0, 2));
  const m = Number(hhmm.slice(3, 5));
  return new Date(Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)), h - 7, m)).toISOString();
}

/** 'HH:MM' giờ VN của thời điểm ISO. */
export function vnHHMM(iso: string | null): string | null {
  if (!iso) return null;
  const m = vnMinuteOfDay(iso);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

export const timeToMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

/** Thứ trong tuần (0 = CN) của ngày YYYY-MM-DD. */
export const weekdayOf = (date: string) => new Date(`${date}T00:00:00Z`).getUTCDay();

/** Danh sách ngày của tháng 'YYYY-MM'. */
export function daysOfMonth(month: string): string[] {
  const y = Number(month.slice(0, 4));
  const mo = Number(month.slice(5, 7));
  const n = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return Array.from({ length: n }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}

/** Giờ kết thúc dùng để tính công: giờ hết công chuẩn nếu có (DEC-532), không thì giờ ra ca. */
export const creditEnd = (rule: Pick<ScheduleRule, 'endTime' | 'creditEndTime'>): string | null => rule.creditEndTime || rule.endTime;

/** Số phút làm chuẩn của ca (giờ hết công chuẩn / giờ ra − giờ vào − nghỉ giữa ca). */
export function standardMinutes(rule: ScheduleRule): number | null {
  const end = creditEnd(rule);
  if (rule.calcMode !== 'hourly' || !rule.startTime || !end) return null;
  return Math.max(0, timeToMin(end) - timeToMin(rule.startTime) - (rule.breakStart ? rule.breakMinutes : 0));
}

/** Số ngày công chuẩn của tháng theo lịch (không tính ngày nghỉ tuần; ngày lễ ở B3). */
export function standardDays(month: string, rule: ScheduleRule | null): number {
  const off = rule?.weeklyOff ?? [0];
  return daysOfMonth(month).filter((d) => !off.includes(weekdayOf(d))).length;
}

const overlap = (a1: number, a2: number, b1: number, b2: number) => Math.max(0, Math.min(a2, b2) - Math.max(a1, b1));
const round2 = (v: number) => Math.round(v * 100) / 100;

/** Ca sáng / chiều của lịch theo giờ có nghỉ trưa (phút trong ngày). null = không chia buổi. */
export function scheduleSessions(rule: ScheduleRule | null): { s: number; bs: number; be: number; e: number; split: number } | null {
  const end = rule ? creditEnd(rule) : null;
  if (!rule || rule.calcMode !== 'hourly' || !rule.startTime || !end || !rule.breakStart || rule.breakMinutes <= 0) return null;
  const s = timeToMin(rule.startTime);
  const e = timeToMin(end);
  const bs = timeToMin(rule.breakStart);
  const be = bs + rule.breakMinutes;
  if (!(s < bs && be < e)) return null;
  // Mốc tách buổi khi chấm 2 lần: giữa giờ nghỉ trưa (VD 11:30–13:00 → 12:15)
  return { s, bs, be, e, split: Math.round((bs + be) / 2) };
}

/** Lượt chấm thuộc buổi nào (lịch tách buổi: kiểu 2, 3). Kiểu 1 → luôn 'am' (cột check_in / check_out). */
export function punchSession(rule: ScheduleRule | null, minuteOfDay: number): 'am' | 'pm' {
  const ss = rule && isSplitPunch(rule.punchMode) ? scheduleSessions(rule) : null;
  return ss && minuteOfDay >= ss.split ? 'pm' : 'am';
}

export interface PunchGates {
  gateInOpen: string | null;
  gateInClose: string | null;
  gateOutOpen: string | null;
  gateOutClose: string | null;
}

/**
 * Cổng giờ tự chấm (P21 C1). Cổng để trống = không chặn.
 * - Vào (buổi sáng / chấm 1 lần): trước giờ mở → chặn; sau giờ đóng → chặn "Đã quá giờ chấm vào, liên hệ văn phòng".
 *   Chấm 2 lần: lượt vào buổi chiều không theo cổng vào (cổng vào là của đầu ngày).
 * - Ra: sau giờ đóng cổng ra → chặn (tự chốt ra ca đã lo phần quên chấm). Ra sớm trước giờ mở cổng ra vẫn cho chấm để ghi nhận về sớm.
 * Trả về thông báo lỗi hoặc null.
 */
export function punchGateError(g: PunchGates, kind: 'in' | 'out', session: 'am' | 'pm', minuteOfDay: number): string | null {
  if (kind === 'in' && session === 'am') {
    if (g.gateInOpen && minuteOfDay < timeToMin(g.gateInOpen)) return `Chưa tới giờ chấm vào (mở lúc ${g.gateInOpen.slice(0, 5)}).`;
    if (g.gateInClose && minuteOfDay > timeToMin(g.gateInClose)) return 'Đã quá giờ chấm vào, liên hệ văn phòng';
  }
  if (kind === 'out' && g.gateOutClose && minuteOfDay > timeToMin(g.gateOutClose)) return `Đã quá giờ chấm ra (cổng ra đóng lúc ${g.gateOutClose.slice(0, 5)}), liên hệ văn phòng sửa giờ ra.`;
  return null;
}

interface SpanResult {
  worked: number;
  late: number;
  early: number;
  after: number;
}

/** Giờ làm trong khung [a, b] (trừ nghỉ [bs, be] nếu có), trễ / về sớm theo dung sai. o đã ≥ i. */
function span(i: number, o: number, a: number, b: number, lateGrace: number, earlyGrace: number, brk: [number, number] | null): SpanResult {
  // C2: ra trong dung sai cuối ca = đủ giờ
  const oEff = o < b && o >= b - earlyGrace ? b : o;
  let worked = overlap(i, oEff, a, b);
  if (brk) worked -= overlap(Math.max(i, a), Math.min(oEff, b), brk[0], brk[1]);
  return {
    worked: Math.max(0, worked),
    late: i > a + lateGrace ? Math.min(i, b) - a : 0,
    early: oEff < b ? b - Math.max(oEff, a) : 0,
    after: Math.max(0, o - Math.max(b, i)),
  };
}

/**
 * Công của một ngày.
 * - Có ghi đè `dayFraction` → lấy số đó (tổ trưởng ghi nửa ngày, văn phòng sửa).
 * - Lịch theo ngày hoặc chưa có lịch: có giờ vào hoặc giờ ra = 1 công.
 * - Lịch theo giờ: giờ làm trong ca (trừ nghỉ giữa ca) / giờ chuẩn, tối đa 1; thiếu giờ vào hoặc ra → 0 công + cờ để văn phòng sửa.
 *   Ra trong dung sai cuối ca (earlyGraceMinutes) = đủ giờ. Lịch có tự chốt: quá giờ hết ca + X phút chưa chấm ra → ra = giờ hết ca, cờ auto_closed.
 * - Vào sáng – vào chiều (kiểu 3, DEC-248): mỗi lần chấm vào được đủ giờ buổi đó (sáng / chiều), trễ tính riêng từng buổi, không có về sớm.
 * - Chấm 2 lần/ngày: buổi sáng (checkIn/checkOut) và buổi chiều (pmCheckIn/pmCheckOut) tính riêng rồi cộng.
 *   Dòng chỉ có 1 cặp vào–ra trải qua trưa (tổ trưởng chấm, nhận/trả xe, văn phòng nhập) tính như chấm 1 lần.
 * `now` (ISO) để biết đã quá giờ tự chốt chưa.
 */
export function computeDay(rule: ScheduleRule | null, date: string, rec: DayRecord | null, now: string = new Date().toISOString()): DayResult {
  const offDay = (rule?.weeklyOff ?? [0]).includes(weekdayOf(date));
  const flags = new Set<AttendanceFlag>();
  for (const f of rec?.flags ?? []) if (f in ATTENDANCE_FLAG_LABELS) flags.add(f as AttendanceFlag);
  const out: DayResult = {
    work: 0, workedMinutes: null, lateMinutes: 0, earlyMinutes: 0, afterMinutes: 0, otHours: rec?.otHours ?? 0, offDay, flags: [], sessions: null,
  };
  if (!rec) return out;
  const pmIn = rec.pmCheckIn ?? null;
  const pmOut = rec.pmCheckOut ?? null;
  const present = !!(rec.checkIn || rec.checkOut || pmIn || pmOut);
  if (present && offDay) flags.add('off_day');

  const std = rule ? standardMinutes(rule) : null;
  if (rule && std && rule.startTime && rule.endTime) {
    const s = timeToMin(rule.startTime);
    // e = mốc hết công (DEC-532: giờ hết công chuẩn nếu có); w = giờ ra ca, mốc tự chốt khi quên chấm ra
    const e = timeToMin(creditEnd(rule)!);
    const w = timeToMin(rule.endTime);
    const ss = scheduleSessions(rule);
    const lateG = rule.lateGraceMinutes;
    const earlyG = rule.earlyGraceMinutes ?? 0;
    // DEC-820: mốc cuối ngày dùng dung sai riêng (nhóm được về sớm); ra trưa (chấm 4 lần) vẫn theo dung sai của lịch
    const endG = endOfDayGrace(rule);
    const nowT = Date.parse(now);
    /** Đã quá giờ tự chốt của mốc hết ca (phút trong ngày) chưa */
    const autoCloseDue = (endMin: number) =>
      rule.autoCloseMinutes !== null && rule.autoCloseMinutes !== undefined && nowT >= Date.parse(date + 'T00:00:00+07:00') + (endMin + rule.autoCloseMinutes) * 60_000;
    const minOf = (iso: string | null) => (iso ? vnMinuteOfDay(iso) : null);
    /** Giờ vào / ra của 1 buổi; thiếu ra mà đã quá giờ tự chốt → ra = hết buổi */
    const pair = (inIso: string | null, outIso: string | null, endMin: number, closeAt = endMin): { i: number | null; o: number | null } => {
      const i = minOf(inIso);
      let o = minOf(outIso);
      if (i !== null && o !== null && o < i) o += 1440; // ra qua nửa đêm
      if (i !== null && o === null) {
        if (i < endMin && autoCloseDue(closeAt)) {
          o = endMin;
          flags.add('auto_closed');
        } else flags.add('missing_out');
      }
      if (i === null && o !== null) flags.add('missing_in');
      return { i, o };
    };
    const amOutMin = minOf(rec.checkOut);
    // DEC-763: giờ ra (sáng) đã qua giữa giờ nghỉ trưa mà lượt "vào chiều" nằm trong khoảng vào–ra (văn phòng sửa thành 1 cặp vào–ra,
    // hoặc chấm thêm giữa ngày) → tính 1 cặp vào → ra muộn nhất, không tách buổi (trước đây ra 0,5 công dù đã sửa đủ ngày).
    const pmInMin = minOf(pmIn);
    const spansLunch = !!ss && amOutMin !== null && amOutMin > ss.split && (pmInMin === null || pmInMin <= amOutMin);
    const twoPunch = rule.punchMode === 2 && ss && !spansLunch && (pmIn || pmOut || !rec.checkOut || (amOutMin !== null && amOutMin <= ss.split));
    const lastOut = spansLunch && pmOut && Date.parse(pmOut) > Date.parse(rec.checkOut!) ? pmOut : rec.checkOut;
    // Kiểu 3; dòng có giờ ra (tổ trưởng chấm, văn phòng nhập cặp vào–ra) tính như kiểu 1
    if (rule.punchMode === 3 && ss && !rec.checkOut) {
      const amStd = ss.bs - ss.s;
      const pmStd = ss.e - ss.be;
      const ai = minOf(rec.checkIn);
      const pi = minOf(pmIn);
      const amW = ai !== null && ai < ss.bs ? amStd : 0;
      const pmW = pi !== null && pi < e ? pmStd : 0;
      if (ai !== null && ai > s + lateG) out.lateMinutes += Math.min(ai, ss.bs) - s;
      if (pi !== null && pi > ss.be + lateG) out.lateMinutes += Math.min(pi, e) - ss.be;
      out.workedMinutes = amW + pmW;
      out.work = round2(Math.min(1, (amW + pmW) / std));
      out.sessions = { am: round2(amW / std), pm: round2(pmW / std) };
    } else if (twoPunch && ss) {
      const am = pair(rec.checkIn, rec.checkOut, ss.bs);
      const pm = pair(pmIn, pmOut, e, w);
      let worked = 0;
      let amW = 0;
      let pmW = 0;
      let complete = false;
      if (am.i !== null && am.o !== null) {
        const r = span(am.i, am.o, s, ss.bs, lateG, earlyG, null);
        amW = r.worked;
        out.lateMinutes += r.late;
        out.earlyMinutes += r.early;
        complete = true;
      } else if (am.i !== null) out.lateMinutes += am.i > s + lateG ? Math.min(am.i, ss.bs) - s : 0;
      if (pm.i !== null && pm.o !== null) {
        const r = span(pm.i, pm.o, ss.be, e, lateG, endG, null);
        pmW = r.worked;
        out.lateMinutes += r.late;
        out.earlyMinutes += r.early;
        out.afterMinutes = r.after;
        complete = true;
      } else if (pm.i !== null) out.lateMinutes += pm.i > ss.be + lateG ? Math.min(pm.i, e) - ss.be : 0;
      worked = amW + pmW;
      if (complete) {
        out.workedMinutes = worked;
        out.work = round2(Math.min(1, worked / std));
      }
      out.sessions = { am: round2(amW / std), pm: round2(pmW / std) };
    } else {
      const { i, o } = pair(rec.checkIn, lastOut, e, w);
      if (i !== null && o !== null) {
        const r = span(i, o, s, e, lateG, endG, ss ? [ss.bs, ss.be] : rule.breakStart && rule.breakMinutes > 0 ? [timeToMin(rule.breakStart), timeToMin(rule.breakStart) + rule.breakMinutes] : null);
        out.workedMinutes = r.worked;
        out.work = round2(Math.min(1, r.worked / std));
        out.lateMinutes = r.late;
        out.earlyMinutes = r.early;
        out.afterMinutes = r.after;
        if (ss) {
          const oEff = o < e && o >= e - endG ? e : o;
          out.sessions = { am: round2(overlap(i, oEff, s, ss.bs) / std), pm: round2(overlap(i, oEff, ss.be, e) / std) };
        }
      } else if (i !== null) {
        out.lateMinutes = i > s + lateG ? Math.min(i, e) - s : 0;
      }
    }
    // DEC-532: lịch có giờ hết công chuẩn không tính ngoài giờ (tăng ca chỉ theo lệnh)
    if (rule.creditEndTime) out.afterMinutes = 0;
  } else if (present) {
    out.work = 1;
  }
  if (rec.dayFraction !== null && rec.dayFraction !== undefined) {
    out.work = round2(rec.dayFraction);
    if (out.sessions) out.sessions = null;
  }
  // DEC-347: ngày chuyển từ CRM – CRM đã trừ trễ / về sớm vào số công và chỉ trả tăng ca theo lệnh → không tính lại trễ, về sớm, ngoài giờ
  if (rec.source === 'crm') {
    out.lateMinutes = 0;
    out.earlyMinutes = 0;
    out.afterMinutes = 0;
  }
  out.flags = [...flags];
  return out;
}

export interface MonthTotals {
  work: number;
  presentDays: number;
  lateCount: number;
  lateMinutes: number;
  earlyMinutes: number;
  otHours: number;
  flagged: number;
}

export function sumMonth(days: DayResult[]): MonthTotals {
  const t: MonthTotals = { work: 0, presentDays: 0, lateCount: 0, lateMinutes: 0, earlyMinutes: 0, otHours: 0, flagged: 0 };
  for (const d of days) {
    t.work += d.work;
    if (d.work > 0) t.presentDays += 1;
    if (d.lateMinutes > 0) t.lateCount += 1;
    t.lateMinutes += d.lateMinutes;
    t.earlyMinutes += d.earlyMinutes;
    t.otHours += d.otHours;
    if (d.flags.some((f) => f === 'missing_in' || f === 'missing_out' || f === 'no_gps' || f === 'offline')) t.flagged += 1;
  }
  t.work = round2(t.work);
  t.otHours = round2(t.otHours);
  return t;
}

// ---------------------------------------------------------------------------
// Ký hiệu ô bảng công tháng (giao diện kiểu minhtien-crm + file Excel). Chỉ hiển thị, không đổi cách tính công.
// ---------------------------------------------------------------------------
export const ATTENDANCE_CELL_STATUS_LABELS = {
  full: 'Đủ công',
  half: 'Nửa công / thiếu giờ',
  late: 'Đi trễ / về sớm',
  problem: 'Cần xem (thiếu giờ vào/ra, không GPS, gửi trễ)',
  absent: 'Vắng',
  working: 'Đã vào, chưa chấm ra',
  pending: 'Chưa chấm',
  off: 'Chủ nhật / ngày nghỉ',
  sunday_work: 'Làm Chủ nhật (lệnh đã xác nhận, tính tiền riêng)',
  future: 'Chưa tới',
  leave: 'Nghỉ có phép, có lương (P)',
  leave_unpaid: 'Nghỉ có phép, không lương (KL)',
} as const;
export type AttendanceCellStatus = keyof typeof ATTENDANCE_CELL_STATUS_LABELS;

export interface AttendanceCellInput {
  work: number;
  offDay: boolean;
  flags: readonly string[];
  lateMinutes: number;
  earlyMinutes: number;
  recordId: number | null;
  checkIn: string | null;
  checkOut: string | null;
  dayFraction: number | null;
  /** P21 PR 4: đơn nghỉ đã duyệt của ngày (null = không nghỉ) */
  leave?: DayLeave | null;
  /** DEC-246: đơn xác thực đã duyệt của ngày (đi trễ / về nửa chừng / điều đi) */
  excuse?: readonly { kind: string }[] | null;
  /** DEC-395: ngày có lệnh Làm Chủ nhật đã xác nhận (tính tiền ở khoản Làm CN, không cộng công thường) */
  sundayWork?: boolean;
}

/** Nghỉ có phép đã duyệt trong 1 ngày (P21 PR 4, DEC-195). */
export interface DayLeave {
  requestId: number;
  /** Có lương → tính công; không lương → không công */
  paid: boolean;
  /** 1 = cả ngày, 0,5 = nửa ngày */
  fraction: number;
  /** Ký hiệu bảng công theo loại nghỉ: P, KL, Ô… */
  symbol: string;
  typeName: string;
  /** Mã loại nghỉ (ANNUAL, COMPANY…); ô lễ không có */
  typeCode?: string;
  /** Đơn máy tạo theo đợt "Công ty cho nghỉ" / ngày lễ trừ phép (DEC-811: không tính là NV nghỉ) */
  companyBatch?: boolean;
}

/**
 * Cộng phép đã duyệt vào công của ngày: có lương → công = min(1, công chấm + phần nghỉ); không lương → giữ công chấm.
 * Ngày nghỉ hằng tuần không áp (đơn đã bỏ ngày nghỉ khi tính số ngày).
 */
export function applyLeave<T extends { work: number; offDay: boolean }>(res: T, leave: DayLeave | null | undefined): T {
  if (!leave || res.offDay || !leave.paid) return res;
  return { ...res, work: round2(Math.min(1, res.work + leave.fraction)) };
}

/**
 * Công Chủ nhật (ngày nghỉ hằng tuần) của một ngày – anh Tiến 30/10/2026: không cộng vào Công thường, ghi cột CN theo công thực tế
 * (vd 0,77); lệnh Làm CN đã xác nhận mà không có giờ chấm (admin chỉnh loại ngày CN) = 1 công. Ngày thường = 0.
 */
export function sundayCredit(day: { work: number; offDay: boolean }, hasSundayOrder: boolean): number {
  if (!day.offDay) return 0;
  if (day.work > 0) return day.work;
  return hasSundayOrder ? 1 : 0;
}

const PROBLEM_FLAG_SET = new Set(['missing_in', 'missing_out', 'no_gps', 'offline']);

/** Trạng thái hiển thị của một ô (ngày `date`, hôm nay `today` theo giờ VN). */
export function attendanceCellStatus(c: AttendanceCellInput, date: string, today: string): AttendanceCellStatus {
  // Nghỉ cả ngày có phép, hoặc nửa ngày phép mà không chấm công → ô phép (P / KL)
  if (c.leave && !c.offDay && (c.leave.fraction >= 1 || !c.recordId)) return c.leave.paid ? 'leave' : 'leave_unpaid';
  if (!c.recordId) {
    if (c.offDay) return c.sundayWork ? 'sunday_work' : 'off';
    if (date > today) return 'future';
    return date === today ? 'pending' : 'absent';
  }
  if (date === today && c.checkIn && !c.checkOut && c.dayFraction === null) return 'working';
  if (c.flags.some((f) => PROBLEM_FLAG_SET.has(f))) return 'problem';
  if (c.work <= 0) return c.offDay ? (c.sundayWork ? 'sunday_work' : 'off') : 'absent';
  if (c.lateMinutes > 0 || c.earlyMinutes > 0) return 'late';
  return c.work >= 1 ? 'full' : 'half';
}

/** Số công dạng VN gọn: 1 · 0,5 · 0,75. */
export const formatWork = (v: number) => (Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100).replace('.', ','));

/** Chữ hiện trong ô: số công khi có công, V = vắng, ! = cần xem mà 0 công, – = chưa chấm, CN/N = nghỉ, LCN = làm Chủ nhật, P/KL = nghỉ có phép. */
export function attendanceCellSymbol(status: AttendanceCellStatus, work: number, date: string, leave?: DayLeave | null): string {
  switch (status) {
    case 'leave':
    case 'leave_unpaid': {
      const sym = leave?.symbol ?? (status === 'leave' ? 'P' : 'KL');
      return leave && leave.fraction < 1 ? `${sym}½` : sym;
    }
    case 'absent':
      return 'V';
    case 'pending':
      return '–';
    case 'working':
      return 'Vào';
    case 'future':
      return '';
    case 'sunday_work':
      return 'LCN';
    case 'off':
      return work > 0 ? formatWork(work) : weekdayOf(date) === 0 ? 'CN' : 'N';
    case 'problem':
      return work > 0 ? formatWork(work) : '!';
    default:
      return formatWork(work);
  }
}

/** P21: mặc định khi thêm lịch MỚI trên web (lịch cũ giữ nguyên quy tắc cũ, xem migration 0083). */
export const NEW_SCHEDULE_DEFAULTS = {
  calcMode: 'hourly' as const,
  punchMode: 1 as PunchMode,
  startTime: '07:30',
  breakStart: '11:30',
  breakMinutes: 90,
  endTime: '17:00',
  weeklyOff: [0],
  gateInOpen: '06:00',
  gateInClose: '08:00',
  gateOutOpen: '11:00',
  gateOutClose: '21:00',
  lateGraceMinutes: 30,
  earlyGraceMinutes: 30,
  autoCloseMinutes: 30 as number | null,
  requireWifi: false,
  requirePhoto: true,
  requireGps: false,
};

/** Nhóm lao động mặc định có tài khoản tự chấm; thi công, thời vụ do tổ trưởng chấm hộ (vẫn tạo tay được). */
export const SELF_PUNCH_GROUPS: readonly WorkGroup[] = ['office', 'warehouse', 'driver'];

/**
 * Anh Tiến 05/11/2026 (bảng công tháng): "thêm cột P/L/KL/V" sau cột Công.
 * P = nghỉ phép có lương (đơn), L = lễ có lương (bảng ngày lễ), KL = nghỉ không lương, V = vắng (ô V). Nửa ngày đếm 0,5.
 */
export interface AttendanceOffCounts {
  p: number;
  l: number;
  kl: number;
  v: number;
}

export function attendanceOffCounts(cells: Record<string, AttendanceCellInput>, days: readonly string[], today: string): AttendanceOffCounts {
  const r = { p: 0, l: 0, kl: 0, v: 0 };
  for (const d of days) {
    const c = cells[d];
    if (!c) continue;
    if (c.leave && !c.offDay) {
      // Ngày lễ có lương: đơn giả requestId 0, ký hiệu L (attendance.service)
      if (c.leave.paid && c.leave.requestId === 0 && c.leave.symbol === 'L') r.l += c.leave.fraction;
      else if (c.leave.paid) r.p += c.leave.fraction;
      else r.kl += c.leave.fraction;
    }
    if (attendanceCellStatus(c, d, today) === 'absent') r.v += 1;
  }
  const r2 = (n: number) => Math.round(n * 100) / 100;
  return { p: r2(r.p), l: r2(r.l), kl: r2(r.kl), v: r.v };
}

/** "3 P · 1 L · 2 KL · 1 V" (bỏ mục bằng 0). */
export function formatOffCounts(o: AttendanceOffCounts): string {
  const f = (n: number) => String(n).replace('.', ',');
  return ([['p', 'P'], ['l', 'L'], ['kl', 'KL'], ['v', 'V']] as const)
    .filter(([k]) => o[k] > 0)
    .map(([k, s]) => `${f(o[k])} ${s}`)
    .join(' · ');
}

/** Cột Công bảng công (anh Tiến 05/11/2026): ngày đi làm thật = công − P − L (VD Đủ T10: 27 − 3 P = 24). */
export function workedDays(work: number, off: Pick<AttendanceOffCounts, 'p' | 'l'>): number {
  return Math.max(0, Math.round((work - off.p - off.l) * 100) / 100);
}
